// ==============================================
// Admin › Feedback › one report
// ==============================================
//
// Everything staff need to handle a report from a Scout. A compact header: the
// reference, the title (the first line the customer wrote), and one line of
// what it is (status, kind, severity, app), who sent it and when, and who it
// waits on. The main column is what they wrote and the conversation, oldest
// first: the customer's comments from their Scout, Vortex's replies (which the
// customer sees), internal notes, and every status and owner change, with the
// composer under it. The side column, without boxes: status and owner, the
// report's facts, the customer, and what the Scout reported about itself.
// Choosing Resolved asks first, for an optional reply and note in the
// composer's own colours. On a phone, status and owner come straight after the
// header, and the rest follows the conversation and its composer.

import { Fragment, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Listbox, Transition } from '@headlessui/react';
import {
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  TrashIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { api } from '../../services/api';
import { useAuth } from '../../providers/AuthProvider';
import { ModalShell } from '../../components/ui/ModalShell';
import { Btn } from '../../components/admin/console';
import { ScoutIcon } from '../../components/devices/ScoutIcon';
import { formatDateTime, formatRelativeTime } from '../../utils/formatters';
import {
  FEEDBACK_STATUSES,
  type FeedbackDetail,
  type FeedbackEvent,
  type FeedbackPerson,
  type FeedbackStaffMember,
  type FeedbackStatus,
  type FeedbackVisibility,
} from '../../types/feedback';
import {
  AppLabel,
  Avatar,
  CATEGORY_LOOK,
  CLOSE_REASON_WORDS,
  SEVERITY_INK,
  SEVERITY_LOOK,
  STATUS_LOOK,
  StatusDot,
  WaitingFlag,
  afterFirstLine,
  feedbackRef,
  firstLine,
  personName,
} from './feedbackShared';
import { FeedbackSystemInfo } from './FeedbackSystemInfo';

const LIST = '/admin/feedback';

function errorText(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string; message?: string } }; message?: string } | null;
  return e?.response?.data?.error || e?.response?.data?.message || fallback;
}

function When({ at }: { at: string }) {
  return <time dateTime={at} title={formatDateTime(at)}>{formatRelativeTime(at)}</time>;
}

/** A small uppercase label over a group: the conversation, a side section. */
const label = 'text-[13px] font-semibold uppercase tracking-wider text-slate-400';
/** A side section after the first: a hairline above, no box. */
const sideSection = 'border-t border-slate-700 pt-4';

/** The customer's steps as a list: one per line, their own numbering dropped. One line stays a sentence. */
function stepLines(steps: string): string[] {
  return steps.split('\n').map((l) => l.replace(/^\s*(\d+[.)]|[-*•])\s*/, '').trim()).filter(Boolean);
}

// ---------------------------------------------------------------------------
// The conversation

/** A change or the report's arrival: one quiet line. */
function SystemLine({ icon, children, at }: { icon: ReactNode; children: ReactNode; at: string }) {
  return (
    <li className="flex items-start gap-2.5 px-4 py-0.5 text-[13px] text-slate-400">
      <span aria-hidden="true" className="mt-0.5 shrink-0 text-slate-500">{icon}</span>
      <p className="min-w-0">
        {children} <span className="whitespace-nowrap">· <When at={at} /></span>
      </p>
    </li>
  );
}

const Who = ({ children }: { children: ReactNode }) => <b className="font-medium text-slate-300">{children}</b>;

function statusWord(v: string | null | undefined): string {
  return (v && STATUS_LOOK[v as FeedbackStatus]?.label) || 'none';
}

function ChangeLine({ event, staffName }: { event: FeedbackEvent; staffName: (id: string | null | undefined) => string }) {
  const who = <Who>{personName(event.author) || 'Someone'}</Who>;
  if (event.kind === 'status' && event.from_customer) {
    const reason = event.meta?.reason ? CLOSE_REASON_WORDS[event.meta.reason] : null;
    return (
      <SystemLine icon={<CheckCircleIcon className="h-[14px] w-[14px]" />} at={event.created_at}>
        {who} closed this report{reason ? <>: <span className="text-slate-200">{reason}</span></> : null}
      </SystemLine>
    );
  }
  if (event.kind === 'status') {
    return (
      <SystemLine icon={<ArrowsRightLeftIcon className="h-[14px] w-[14px]" />} at={event.created_at}>
        {who} set it <span className="text-slate-200">{statusWord(event.meta?.to)}</span>
        {event.visibility === 'customer' && <span className="text-cyan-300"> · shown on their Scout</span>}
      </SystemLine>
    );
  }
  const to = event.meta?.to;
  const words = !to ? <>removed the owner</>
    : event.author && to === event.author.id ? <>took it on</>
      : <>made <span className="text-slate-200">{staffName(to)}</span> the owner</>;
  return (
    <SystemLine icon={<UserCircleIcon className="h-[14px] w-[14px]" />} at={event.created_at}>
      {who} {words}
    </SystemLine>
  );
}

type MessageKind = 'customer' | 'reply' | 'internal' | 'legacy';

/** Tinted only where it matters: an internal note, and the customer's own words. A reply to them sits plain. */
const MESSAGE_LOOK: Record<MessageKind, { box: string; tag: ReactNode }> = {
  customer: {
    box: 'border border-slate-700 bg-slate-800',
    tag: <span className="text-slate-300">Customer, from their Scout</span>,
  },
  reply: {
    box: '',
    tag: <span className="font-medium text-cyan-300">Sent to the customer</span>,
  },
  internal: {
    box: 'border border-amber-500/20 bg-amber-500/[0.07]',
    tag: <span className="inline-flex items-center gap-1 font-medium text-amber-300"><LockClosedIcon className="h-[14px] w-[14px]" aria-hidden="true" />Internal note</span>,
  },
  legacy: {
    box: 'border border-slate-700 bg-slate-800/60',
    tag: <span className="text-slate-300">Earlier reply (old system)</span>,
  },
};

function Message({ kind, author, at, body }: { kind: MessageKind; author?: FeedbackPerson | null; at?: string; body: string }) {
  const look = MESSAGE_LOOK[kind];
  return (
    <li className={clsx('grid grid-cols-[28px_minmax(0,1fr)] gap-x-3 rounded-lg px-4 py-3.5', look.box)} data-kind={kind}>
      {author ? <Avatar person={author} size="sm" /> : (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-700"><ScoutIcon className="h-4 w-4 text-slate-300" /></span>
      )}
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
          {author && <span className="font-semibold text-white">{personName(author)}</span>}
          {look.tag}
          {at && <span className="ml-auto text-slate-500"><When at={at} /></span>}
        </div>
        <p className="mt-1.5 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-slate-100">{body}</p>
      </div>
    </li>
  );
}

/**
 * How words for the customer and words for staff look wherever they are written:
 * the composer and the "Mark resolved" box. Cyan reaches the customer's Scout;
 * amber stays with staff.
 */
const WRITE_LOOK: Record<FeedbackVisibility, { box: string; tab: string; Icon: typeof LockClosedIcon | typeof ScoutIcon }> = {
  customer: { box: 'border-cyan-700 focus-within:border-cyan-500', tab: 'bg-cyan-900 text-cyan-50', Icon: ScoutIcon },
  internal: { box: 'border-amber-700/60 focus-within:border-amber-500/80', tab: 'bg-amber-900/50 text-amber-100', Icon: LockClosedIcon },
};
const writeBox = 'rounded-lg border bg-[#0B1324] transition-colors';
const writeTab = 'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-sm font-medium';
const writeArea = 'block w-full resize-y border-0 bg-transparent px-3.5 py-3 text-[15px] leading-relaxed text-white placeholder:text-slate-500 focus:outline-none focus:ring-0';
const writeFoot = 'border-t border-slate-800 py-2 pl-3.5 pr-2';

function Composer({ onPost, pending, error, customerName, hasAccount }: {
  onPost: (body: string, visibility: FeedbackVisibility, done: () => void) => void;
  pending: boolean;
  error: string | null;
  /** How the hint names the customer: their first name, or "The customer". */
  customerName: string;
  /** A report from before accounts reaches nobody: there is no one to reply to. */
  hasAccount: boolean;
}) {
  const [draft, setDraft] = useState('');
  const [visibility, setVisibility] = useState<FeedbackVisibility>('internal');
  const submit = () => {
    if (draft.trim() && !pending) onPost(draft.trim(), visibility, () => setDraft(''));
  };
  const toCustomer = visibility === 'customer';
  const hint = toCustomer
    ? `${customerName} sees this on their Scout, under My feedback. Ctrl+Enter sends.`
    : 'Only Vortex staff see this. Ctrl+Enter sends.';
  const tabs: { v: FeedbackVisibility; text: string }[] = [
    { v: 'customer', text: 'Reply to customer' },
    { v: 'internal', text: 'Internal note' },
  ];
  return (
    <form
      className={clsx(writeBox, WRITE_LOOK[visibility].box)}
      onSubmit={(e) => { e.preventDefault(); submit(); }}
    >
      <div role="radiogroup" aria-label="Who sees this" className="flex gap-1 px-2 pt-2">
        {tabs.map(({ v, text }) => {
          const { Icon } = WRITE_LOOK[v];
          const on = visibility === v;
          const off = v === 'customer' && !hasAccount;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={off}
              title={off ? 'This report came before accounts, so nobody would see a reply' : undefined}
              onClick={() => setVisibility(v)}
              className={clsx(
                writeTab, 'transition-colors disabled:cursor-not-allowed disabled:text-slate-600',
                on ? WRITE_LOOK[v].tab : 'text-slate-400 hover:text-slate-100',
              )}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {text}
            </button>
          );
        })}
      </div>
      <label className="block">
        <span className="sr-only">{toCustomer ? 'Reply to customer' : 'Internal note'}</span>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          rows={3}
          placeholder={toCustomer ? `Write to ${customerName === 'The customer' ? 'the customer' : customerName}` : 'Add a note for the team'}
          className={writeArea}
        />
      </label>
      {error && <p role="alert" className="px-3.5 pb-2 text-sm text-red-300">{error}</p>}
      <div className={clsx('flex flex-wrap items-center justify-between gap-3', writeFoot)}>
        <p className="text-[13px] text-slate-400">{hint}</p>
        <Btn type="submit" variant="primary" size="sm" disabled={!draft.trim() || pending} leftIcon={<PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />}>
          {pending ? 'Sending' : toCustomer ? 'Send reply' : 'Add note'}
        </Btn>
      </div>
    </form>
  );
}

/** One optional box in "Mark resolved": the composer's look, its tab as the label, its hint under the words. */
function WordsField({ look, label: text, value, onChange, onSubmit, placeholder, hint, disabled = false }: {
  look: FeedbackVisibility;
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** Ctrl+Enter: the box's own button. */
  onSubmit: () => void;
  placeholder: string;
  hint: string;
  disabled?: boolean;
}) {
  const { box, tab, Icon } = WRITE_LOOK[look];
  return (
    <label className={clsx(writeBox, disabled ? 'border-slate-700' : box, 'block')}>
      <span className="flex items-center gap-2 px-2 pt-2">
        <span className={clsx(writeTab, disabled ? 'text-slate-500' : tab)}>
          <Icon className="h-4 w-4" aria-hidden="true" />
          {text}
        </span>
        <span className="text-[13px] text-slate-400">Optional</span>
      </span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSubmit(); } }}
        rows={3}
        maxLength={5000}
        disabled={disabled}
        placeholder={placeholder}
        className={clsx(writeArea, 'disabled:cursor-not-allowed')}
      />
      <span className={clsx('block text-[13px] text-slate-400', writeFoot)}>{hint}</span>
    </label>
  );
}

/**
 * Choosing Resolved asks first: an optional reply to the customer and an optional
 * note for staff. Cancel leaves the report as it was. Mark resolved saves the words
 * and the status in one write (PATCH with reply and note), so neither lands alone.
 */
function ResolveDialog({ open, reference, customerName, hasAccount, pending, error, onCancel, onResolve }: {
  open: boolean;
  reference: string;
  /** Their first name, or "The customer". */
  customerName: string;
  hasAccount: boolean;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onResolve: (words: { reply: string; note: string }) => void;
}) {
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  // Each time it opens, it opens empty.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) { setReply(''); setNote(''); }
  }
  const resolve = () => { if (!pending) onResolve({ reply: reply.trim(), note: note.trim() }); };
  const them = customerName === 'The customer' ? 'the customer' : customerName;
  return (
    <ModalShell
      open={open}
      onClose={() => !pending && onCancel()}
      title={<>Mark <span className="font-mono text-[15px]">{reference}</span> resolved</>}
      description={hasAccount
        ? `${customerName} sees it as Resolved on their Scout.`
        : 'This report came before accounts, so no Scout shows it.'}
      size="md"
      closeDisabled={pending}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Btn variant="secondary" size="md" onClick={onCancel} disabled={pending}>Cancel</Btn>
          <Btn variant="primary" size="md" onClick={resolve} disabled={pending} leftIcon={<CheckCircleIcon className="h-4 w-4" aria-hidden="true" />}>
            {pending ? 'Saving' : 'Mark resolved'}
          </Btn>
        </div>
      }
    >
      <div className="space-y-4">
        <WordsField
          look="customer"
          label={`Reply to ${them}`}
          value={reply}
          onChange={setReply}
          onSubmit={resolve}
          disabled={!hasAccount}
          placeholder={hasAccount ? `Write to ${them}` : 'Nobody would see a reply'}
          hint={hasAccount ? `${customerName} sees this on their Scout, under My feedback.` : 'This report came before accounts, so nobody would see a reply.'}
        />
        <WordsField
          look="internal"
          label="Internal note"
          value={note}
          onChange={setNote}
          onSubmit={resolve}
          placeholder="Add a note for the team"
          hint="Only Vortex staff see this. Ctrl+Enter marks it resolved."
        />
        {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// The side column

const pickerButton =
  'relative flex h-9 w-full items-center gap-2 rounded-md border border-slate-700 bg-slate-900 pl-2.5 pr-8 text-left text-sm text-white hover:border-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 disabled:cursor-wait disabled:opacity-60';
const pickerOptions =
  'absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md bg-slate-800 py-1 text-sm shadow-xl ring-1 ring-slate-600 focus:outline-none';

function PickerOption<T>({ value, children }: { value: T; children: ReactNode }) {
  return (
    <Listbox.Option value={value} as={Fragment}>
      {({ active, selected }) => (
        <li className={clsx('flex cursor-pointer select-none items-center gap-2 px-3 py-2', active ? 'bg-slate-700 text-white' : 'text-slate-200')}>
          {children}
          {selected && <CheckIcon className="ml-auto h-4 w-4 text-cyan-300" aria-hidden="true" />}
        </li>
      )}
    </Listbox.Option>
  );
}

function Picker<T>({ title, aside, value, onChange, disabled, shown, children }: {
  title: string;
  /** Beside the label, on its right: Assign to me. */
  aside?: ReactNode;
  value: T;
  onChange: (v: T) => void;
  disabled?: boolean;
  shown: ReactNode;
  children: ReactNode;
}) {
  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className="relative">
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          <Listbox.Label className={label}>{title}</Listbox.Label>
          {aside}
        </div>
        <Listbox.Button className={pickerButton}>
          {shown}
          <ChevronDownIcon className="pointer-events-none absolute right-2.5 h-4 w-4 text-slate-400" aria-hidden="true" />
        </Listbox.Button>
        <Transition as={Fragment} leave="transition ease-in duration-75" leaveFrom="opacity-100" leaveTo="opacity-0">
          <Listbox.Options className={pickerOptions}>{children}</Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

function Fact({ label: name, children }: { label: string; children: ReactNode }) {
  return (
    <div className="contents">
      <dt className="text-slate-400">{name}</dt>
      <dd className="min-w-0 break-words text-right text-slate-100">{children}</dd>
    </div>
  );
}

const facts = 'grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm';

// ---------------------------------------------------------------------------

export function AdminFeedbackDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // The list hands over its own address (filters and page) so Back returns to it.
  const backTo = (location.state as { from?: string } | null)?.from ?? LIST;
  const here = location.pathname + location.search;

  const detailKey = ['admin', 'feedback', 'detail', id];
  const { data: fb, isLoading, isError, refetch } = useQuery({
    queryKey: detailKey,
    queryFn: () => api.adminFeedback(id),
    enabled: !!id,
  });
  const { data: staff = [] } = useQuery({
    queryKey: ['admin', 'feedback', 'staff'],
    queryFn: () => api.adminFeedbackStaff(),
    staleTime: 5 * 60_000,
  });

  const staffName = (uid: string | null | undefined): string => {
    if (!uid) return 'nobody';
    const s = staff.find((m) => m.id === uid);
    if (s) return personName(s);
    if (fb?.assignee?.id === uid) return personName(fb.assignee);
    return 'someone no longer on staff';
  };

  const refreshList = () => queryClient.invalidateQueries({ queryKey: ['admin', 'feedback', 'list'] });

  const update = useMutation({
    mutationFn: (input: { status?: FeedbackStatus; assignee_user_id?: string | null }) => api.adminFeedbackUpdate(id, input),
    onSuccess: (next: FeedbackDetail) => {
      queryClient.setQueryData(detailKey, next);
      refreshList();
    },
  });

  // Resolved asks first (ResolveDialog); its words go in the same PATCH.
  const [resolving, setResolving] = useState(false);
  const resolve = useMutation({
    mutationFn: (words: { reply: string; note: string }) =>
      api.adminFeedbackUpdate(id, { status: 'resolved', ...(words.reply ? { reply: words.reply } : {}), ...(words.note ? { note: words.note } : {}) }),
    onSuccess: (next: FeedbackDetail) => {
      queryClient.setQueryData(detailKey, next);
      refreshList();
      setResolving(false);
    },
  });

  const post = useMutation({
    mutationFn: (input: { body: string; visibility: FeedbackVisibility; done: () => void }) =>
      api.adminFeedbackComment(id, { body: input.body, visibility: input.visibility }),
    onSuccess: (_ev, input) => {
      input.done();
      queryClient.invalidateQueries({ queryKey: detailKey });
      refreshList();
    },
  });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.adminFeedbackDelete(id),
    onSuccess: () => {
      setConfirmDelete(false);
      queryClient.removeQueries({ queryKey: detailKey });
      refreshList();
      navigate(backTo, { replace: true });
    },
  });

  const crumbs = (ref?: string) => (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-slate-400">
      <Link to={backTo} className="font-medium text-slate-300 hover:text-white">Feedback</Link>
      {ref && <><span aria-hidden="true">/</span><span className="font-mono text-[13px]" aria-current="page">{ref}</span></>}
    </nav>
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <ArrowPathIcon className="h-6 w-6 animate-spin text-slate-400" aria-hidden="true" />
      </div>
    );
  }

  if (isError || !fb) {
    return (
      <div className="mx-auto max-w-6xl space-y-4">
        {crumbs()}
        <div role="alert" className="rounded-lg border border-slate-700 bg-slate-800/50 p-6 text-[15px] text-slate-200">
          This report did not load. It may have been deleted.{' '}
          <button type="button" className="font-medium text-cyan-300 underline" onClick={() => refetch()}>Try again</button>
        </div>
      </div>
    );
  }

  const ref = feedbackRef(fb.reference);
  const title = firstLine(fb.description) || `${CATEGORY_LOOK[fb.category]?.label ?? 'Feedback'} report`;
  const body = afterFirstLine(fb.description);
  const senderName = fb.account ? personName(fb.account) : null;
  const firstName = fb.account?.name?.trim().split(/\s+/)[0] || 'The customer';
  const events = fb.events ?? [];
  const status = STATUS_LOOK[fb.status] ?? STATUS_LOOK.open;
  const severity = fb.category === 'bug' && fb.severity ? SEVERITY_LOOK[fb.severity] : null;
  const steps = fb.steps_to_reproduce?.trim() ? stepLines(fb.steps_to_reproduce) : [];
  const dot = <span aria-hidden="true" className="text-slate-600">·</span>;

  // The owner choices: staff, plus the current owner if they have left the list.
  const ownerChoices: FeedbackStaffMember[] = [...staff];
  if (fb.assignee && !ownerChoices.some((s) => s.id === fb.assignee!.id)) {
    ownerChoices.push({ ...fb.assignee, role: '' });
  }
  const mine = !!user && fb.assignee?.id === user.id;

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      {crumbs(ref)}

      <div className="grid gap-x-8 gap-y-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        {/* What it is */}
        <header className="order-1 min-w-0 space-y-2.5 border-b border-slate-700 pb-5 lg:col-span-2">
          <h1 className="break-words text-[22px] font-semibold leading-snug text-white">{title}</h1>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-slate-300">
            <StatusDot status={fb.status} />
            {dot}
            <span>{(CATEGORY_LOOK[fb.category] ?? CATEGORY_LOOK.other).label}</span>
            {severity && <>{dot}<span className={SEVERITY_INK[fb.severity!]}>{severity.label}</span></>}
            {fb.affected_app && <>{dot}<AppLabel app={fb.affected_app} size={18} /></>}
            {dot}
            <span className="text-slate-400">
              {senderName ?? 'Sender not known'}{fb.vessel_name && <> on {fb.vessel_name}</>}, <When at={fb.created_at} />
            </span>
            <WaitingFlag row={fb} className="ml-1.5" />
          </p>
        </header>

        {/* The main column: what they wrote, the conversation, the composer */}
        <div className="order-3 min-w-0 space-y-6 lg:col-start-1 lg:row-start-2">
          {(body || steps.length > 0) && (
            <section aria-label="The report" className="space-y-3.5">
              {body && <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-slate-100">{body}</p>}
              {steps.length > 0 && (
                <div>
                  <h2 className={clsx(label, 'mb-1.5')}>Steps to make it happen</h2>
                  {steps.length > 1 ? (
                    <ol className="list-decimal space-y-0.5 pl-5 text-[15px] leading-relaxed text-slate-100">
                      {steps.map((s, i) => <li key={i} className="break-words">{s}</li>)}
                    </ol>
                  ) : (
                    <p className="break-words text-[15px] leading-relaxed text-slate-100">{steps[0]}</p>
                  )}
                </div>
              )}
            </section>
          )}

          <section aria-labelledby="fb-talk-heading" className="space-y-2">
            <h2 id="fb-talk-heading" className={clsx(label, 'mb-1.5')}>Conversation</h2>
            <ol aria-label="Timeline" className="space-y-2">
              <SystemLine icon={<ClockIcon className="h-[14px] w-[14px]" />} at={fb.created_at}>
                <Who>{senderName ?? 'The customer'}</Who> sent this{fb.vessel_name && <> from {fb.vessel_name}</>}
              </SystemLine>
              {fb.notes?.trim() && <Message kind="legacy" body={fb.notes} />}
              {events.map((ev) => {
                if (ev.kind === 'comment') {
                  return (
                    <Message
                      key={ev.id}
                      kind={ev.from_customer ? 'customer' : ev.visibility === 'customer' ? 'reply' : 'internal'}
                      author={ev.author}
                      at={ev.created_at}
                      body={ev.body ?? ''}
                    />
                  );
                }
                // The customer closing it: the line, then their note in their own words.
                if (ev.kind === 'status' && ev.from_customer && ev.body?.trim()) {
                  return (
                    <Fragment key={ev.id}>
                      <ChangeLine event={ev} staffName={staffName} />
                      <Message kind="customer" author={ev.author} at={ev.created_at} body={ev.body} />
                    </Fragment>
                  );
                }
                return <ChangeLine key={ev.id} event={ev} staffName={staffName} />;
              })}
            </ol>
            <div className="pt-2.5">
              <Composer
                onPost={(text, visibility, done) => post.mutate({ body: text, visibility, done })}
                pending={post.isPending}
                error={post.isError ? errorText(post.error, 'That did not send. Try again.') : null}
                customerName={fb.account ? firstName : 'The customer'}
                hasAccount={!!fb.account}
              />
            </div>
          </section>
        </div>

        {/* The side column. On a phone it dissolves into the page (display: contents):
            status and owner straight after the header, the rest after the conversation. */}
        <aside aria-label="Details" className="contents lg:col-start-2 lg:row-start-2 lg:block lg:min-w-0 lg:space-y-5 lg:self-start">
          <section aria-label="Status and owner" className="order-2 grid grid-cols-2 gap-3 lg:block lg:space-y-3">
            <div>
              <Picker<FeedbackStatus>
                title="Status"
                value={fb.status}
                disabled={update.isPending || resolve.isPending}
                onChange={(s) => {
                  if (s === fb.status) return;
                  if (s === 'resolved') { resolve.reset(); setResolving(true); } else update.mutate({ status: s });
                }}
                shown={<><span aria-hidden="true" className={clsx('h-2 w-2 shrink-0 rounded-full', status.dot)} /><span className="truncate">{status.label}</span></>}
              >
                {FEEDBACK_STATUSES.map((s) => {
                  const look = STATUS_LOOK[s];
                  return (
                    <PickerOption key={s} value={s}>
                      <span aria-hidden="true" className={clsx('h-2 w-2 rounded-full', look.dot)} />
                      {look.label}
                    </PickerOption>
                  );
                })}
              </Picker>
              <p className="mt-1.5 hidden text-[13px] leading-snug text-slate-400 lg:block">
                Resolved, or reopening, shows on the customer's Scout. Owner changes never do.
              </p>
            </div>

            <div>
              <Picker<string>
                title="Owner"
                aside={user && !mine ? (
                  <button
                    type="button"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ assignee_user_id: user.id })}
                    className="text-[13px] font-medium text-cyan-300 hover:text-cyan-200 disabled:opacity-50"
                  >
                    Assign to me
                  </button>
                ) : undefined}
                value={fb.assignee?.id ?? ''}
                disabled={update.isPending}
                onChange={(uid) => uid !== (fb.assignee?.id ?? '') && update.mutate({ assignee_user_id: uid || null })}
                shown={<><Avatar person={fb.assignee} size="xs" /><span className="truncate">{fb.assignee ? personName(fb.assignee) : 'Nobody'}</span></>}
              >
                <PickerOption value="">
                  <Avatar person={null} size="xs" />
                  Nobody
                </PickerOption>
                {ownerChoices.map((s) => (
                  <PickerOption key={s.id} value={s.id}>
                    <Avatar person={s} size="xs" />
                    <span className="truncate">{personName(s)}{user?.id === s.id ? ' (you)' : ''}</span>
                  </PickerOption>
                ))}
              </Picker>
              {!fb.assignee && fb.legacy_assigned_to && (
                <p className="mt-1.5 text-[13px] text-slate-400">Old system: {fb.legacy_assigned_to}</p>
              )}
            </div>
            {update.isError && (
              <p role="alert" className="col-span-2 text-sm text-red-300">{errorText(update.error, 'That change did not save. Try again.')}</p>
            )}
          </section>

          <dl className={clsx(facts, sideSection, 'order-4')}>
            <Fact label="Reference"><span className="font-mono text-[13px]">{ref}</span></Fact>
            <Fact label="Sent">{formatDateTime(fb.created_at)}</Fact>
            {fb.updated_at !== fb.created_at && <Fact label="Last activity"><When at={fb.updated_at} /></Fact>}
            {fb.resolved_at && <Fact label={fb.status === 'closed' ? 'Closed' : 'Resolved'}>{formatDateTime(fb.resolved_at)}</Fact>}
          </dl>

          <section aria-labelledby="fb-from-heading" className={clsx(sideSection, 'order-5 space-y-2.5')}>
            <h3 id="fb-from-heading" className={label}>Customer</h3>
            {!fb.account && <p className="text-sm text-slate-400">No account. Sent before reports were tied to one.</p>}
            <dl className={facts}>
              {fb.account?.name && <Fact label="Name">{fb.account.name}</Fact>}
              {fb.account && (
                <Fact label="Account">
                  <Link
                    to={`/admin/users?search=${encodeURIComponent(fb.account.email)}&expand=${encodeURIComponent(fb.account.id)}`}
                    className="text-cyan-300 hover:underline"
                  >
                    {fb.account.email}
                  </Link>
                </Fact>
              )}
              <Fact label="Boat">{fb.vessel_name || <span className="text-slate-400">Not known</span>}</Fact>
              <Fact label="Scout">
                {fb.device ? (
                  <Link to={`/admin/devices/${fb.device.id}`} state={{ from: here }} className="font-mono text-[13px] text-cyan-300 hover:underline">
                    {fb.device.serial_number}
                  </Link>
                ) : <span className="text-slate-400">Not known</span>}
              </Fact>
            </dl>
          </section>

          <FeedbackSystemInfo
            info={fb.system_info}
            fallbackPosition={{ lat: fb.latitude, lon: fb.longitude }}
            className={clsx(sideSection, 'order-6')}
          />

          <div className={clsx(sideSection, 'order-7')}>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1.5 rounded-md py-1 text-sm font-medium text-red-400 hover:text-red-300"
            >
              <TrashIcon className="h-4 w-4" aria-hidden="true" />
              Delete report
            </button>
          </div>
        </aside>
      </div>

      <ResolveDialog
        open={resolving}
        reference={ref}
        customerName={fb.account ? firstName : 'The customer'}
        hasAccount={!!fb.account}
        pending={resolve.isPending}
        error={resolve.isError ? errorText(resolve.error, 'It was not marked resolved. Try again.') : null}
        onCancel={() => setResolving(false)}
        onResolve={(words) => resolve.mutate(words)}
      />

      <ModalShell
        open={confirmDelete}
        onClose={() => !remove.isPending && setConfirmDelete(false)}
        title="Delete this report for good?"
        size="sm"
        closeDisabled={remove.isPending}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Btn variant="secondary" size="md" onClick={() => setConfirmDelete(false)} disabled={remove.isPending}>Keep it</Btn>
            <Btn variant="dangerSolid" size="md" onClick={() => remove.mutate()} disabled={remove.isPending}>
              {remove.isPending ? 'Deleting' : 'Delete report'}
            </Btn>
          </div>
        }
      >
        <p className="text-[15px] leading-relaxed text-slate-200">
          The report, its notes and the whole conversation with the customer are removed, here and on their Scout. This cannot be undone.
        </p>
        {remove.isError && (
          <p role="alert" className="mt-3 text-sm text-red-300">{errorText(remove.error, 'It was not deleted. Try again.')}</p>
        )}
      </ModalShell>
    </div>
  );
}

export default AdminFeedbackDetailPage;
